import { HttpClient, HttpContext, HttpParams } from '@angular/common/http'
import { Inject, Injectable, Optional } from '@angular/core'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import type {
    IChangePatientPhoneModel,
    ICity,
    IClinic,
    IConfirmResetPasswordRequest,
    ICreatePrerecordModel,
    IFamilyMember,
    IInitializeRegisterRequest,
    ILoginRequest,
    IOnlineAnalysisFile,
    IOnlinePrerecord,
    IPatientHistory,
    IRegisterFamilyMemberModel,
    IRegisterRequest,
    IResetPatientPasswordRequest,
    ISpecialization,
    ISpecializationCategory,
    IUpdateUserInformationModel,
    IPatientProfile,
    IValidationResult,
    IUserInformationModel,
    OnlineProtocolInformation,
    OnlineAnalysisInformation,
    IOrderLoginRequest,
    IOrderInformation,
    IFileSharingModel,
    OnlineSchedulerIntervals,
    PatientDiscountDto,
    IOnlineDoctorSlim,
    IOnlineDoctorFull,
    InitializeLoginResponse,
    IAccountContextData,
    IPackageCategorie,
    IPackageSlim,
    IPackageFull,
    ExtendedOnlineSchedules,
    OnlineSchedules,
} from './api.models'
import { APP_PATH, BASE_PATH } from './tokens'
import { withSSrCache } from './ssr-cache'
import { withRecapture } from './re-captcha.context'
import { withSpinner } from './spinner.context'
import { getDayOfYear } from 'date-fns'

export type AppRequestData = {
    context?: HttpContext
    params?:
        | HttpParams
        | {
              [param: string]: string | string[]
          }
}

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    constructor(
        @Inject(BASE_PATH) private baseUrl: string,
        @Inject(APP_PATH) @Optional() private appUrl: string,
        private client: HttpClient
    ) {}

    public getUri(uri: string) {
        const prefix = !!this.baseUrl ? `${this.appUrl ?? ''}/${this.baseUrl}/` : `${this.appUrl ?? ''}/`
        if (uri.startsWith(prefix)) {
            return uri
        }

        if (uri.startsWith('/')) {
            uri = uri.substring(1)
        }

        if (this.baseUrl) {
            return `${this.appUrl ?? ''}/${this.baseUrl}/${uri}`
        } else {
            return `${this.appUrl ?? ''}/${uri}`
        }
    }

    private get<T = any>(uri: string, headers?: AppRequestData) {
        return this.client.get<T>(this.getUri(uri), headers)
    }

    public download(uri: string, context?: HttpContext) {
        return this.client.get(this.getUri(uri), {
            responseType: 'blob',
            context: context,
        })
    }

    private post<T = any, TResponse = any>(uri: string, body: T, options?: AppRequestData) {
        return this.client.post<TResponse>(this.getUri(uri), body, {
            ...options,
        })
    }

    private delete<TResponse = any>(uri: string) {
        return this.client.delete<TResponse>(this.getUri(uri))
    }

    private patch<TResponse = any, T = any>(uri: string, body: T) {
        return this.client.patch<TResponse>(this.getUri(uri), body)
    }

    public getAppContext() {
        return this.get<IAccountContextData>('api/configurations', {
            context: withSSrCache(),
        })
    }

    public clinics(doctorId: number) {
        return this.get<IClinic[]>(`api/clinics/${doctorId}`, {
            context: withSSrCache(),
        })
    }

    public allClinics() {
        return this.get<IClinic[]>(`api/clinics`, {
            context: withSSrCache(),
        })
    }

    public findSchedulers(
        from: Date,
        to: Date,
        clinicId?: number,
        doctorId?: number,
        searchExpr?: string
    ): Observable<OnlineSchedulerIntervals[]> {
        let q = new HttpParams()
        q = q.set('from', from.toISOString())
        q = q.set('to', to.toISOString())

        if (clinicId) {
            q = q.set('clinicId', clinicId)
        }
        if (doctorId) {
            q = q.set('doctorId', doctorId)
        }

        if (searchExpr) {
            q = q.set('searchExpr', searchExpr)
        }

        return this.get<OnlineSchedulerIntervals<ExtendedOnlineSchedules>[]>('api/schedulers', {
            params: q,
            context: withSSrCache(),
        }).pipe(
            map((x) => {
                //in our case we can get two schedules for one day
                //online app does not care about it, but we should merge them
                //we have to add more data to interval (taken from origin interval`s scheduler)
                //then we can only consider interval object (scheduler object will be only for day aggregation)
                //in each we will have all required information, roomId, clinicId, scheduleId and time intervals
                //theoretically we can have two itervals overlapping each other, but it is exeptional case

                for (const d of x) {
                    for (const s of d.schedules) {
                        for (const i of s.freeIntervals) {
                            i.from = new Date(i.from)
                            i.to = new Date(i.to)
                            i.date = new Date(i.date ?? s.date)
                            i.clinicId ??= s.clinicId
                            i.scheduleId ??= s.scheduleId
                            i.roomId ??= s.roomId
                        }
                    }

                    //compare schedulers by day and merge if there are two or more schedulers for one day
                    //merge its intervals into one array (all intervals from different schedulers for one day in one array)
                    d.schedules = d.schedules.reduce((acc, current) => {
                        const x = acc.find((item) => getDayOfYear(item.date) === getDayOfYear(current.date))
                        if (!x) {
                            return acc.concat([current])
                        } else {
                            x.freeIntervals = [...x.freeIntervals, ...current.freeIntervals]
                            return acc
                        }
                    }, [] as OnlineSchedules[]) as any[]

                    //sort intervals by time
                    for (const iterator of d.schedules) {
                        iterator.freeIntervals.sort((a, b) => a.from.getTime() - b.from.getTime())
                    }
                }

                return x
            })
        )
    }

    public specializations() {
        return this.get<ISpecialization[]>('api/specializations', {
            context: withSSrCache(),
        })
    }

    public specializationCategories() {
        return this.get<ISpecializationCategory[]>('api/specializations/categories', {
            context: withSSrCache(),
        })
    }

    public me() {
        return this.get<IPatientProfile | null>('api/me', {
            context: withSSrCache(),
        })
    }

    public countOfPlanned() {
        return this.get<number | null>('api/me/countOfPlanned', {
            context: withSSrCache(),
        })
    }

    public generateTelegramBotLink() {
        return this.post<any, string>('api/me/connect/telegram', {})
    }

    public isTelegramBotConnected() {
        return this.get<boolean>('api/me/connect/telegram')
    }

    public relogin() {
        return this.post('api/account/relogin', {})
    }

    public login(body: ILoginRequest) {
        return this.post('api/account/login', body, {
            context: withRecapture('login'),
        })
    }

    public initializeOtp(body: ILoginRequest) {
        return this.post<ILoginRequest, InitializeLoginResponse>('api/account/initialize_otp', body, {
            context: withRecapture('initializeOtp'),
        })
    }

    public orderLogin(body: IOrderLoginRequest, context?: HttpContext) {
        return this.post('api/order/login', body, {
            context: context,
        })
    }

    public shareLogin(body: IOrderLoginRequest) {
        return this.post('api/file-sharing/login', body, {
            context: withRecapture('fileSharingLogin'),
        })
    }
    public getShareLinks() {
        return this.get<IFileSharingModel[]>('api/file-sharing/get-study-download-url')
    }

    public logoff() {
        return this.post('api/account/logoff', {})
    }

    public resetPassword(body: IResetPatientPasswordRequest) {
        return this.post('api/account/reset', body, {
            context: withRecapture('resetPassword'),
        })
    }

    public confirmResetPassword(body: IConfirmResetPasswordRequest) {
        return this.post('api/account/reset/confirm', body)
    }

    public initializeRegister(body: IInitializeRegisterRequest) {
        return this.post('api/account/register/initialize', body, {
            context: withRecapture('initializeRegister'),
        })
    }

    public register(body: IRegisterRequest) {
        return this.post('api/account/register', body, {
            context: withRecapture('register'),
        })
    }

    public cities(city: string) {
        return this.get<ICity[]>('api/cities', {
            params: {
                query: city,
            },
        })
    }

    public city(cityId: number) {
        return this.get<ICity>(`api/cities/${cityId}`, {
            context: withSSrCache(),
        })
    }

    public sendOpt(request: IInitializeRegisterRequest) {
        return this.post(`api/me/initialize_otp`, request, {
            context: withRecapture('sendOpt'),
        })
    }

    public updateInformation(model: IUpdateUserInformationModel) {
        return this.post('api/me/update', model)
    }

    public changePhone(model: IChangePatientPhoneModel) {
        return this.post('api/me/changePhone', model)
    }

    public prerecordsHistory(patientId: number | null) {
        if (patientId && patientId > 0) {
            return this.get<IOnlinePrerecord[]>(`api/me/prerecords?patientId=${patientId}`, {
                context: withSSrCache(),
            })
        } else {
            return this.get<IOnlinePrerecord[]>(`api/me/prerecords`, {
                context: withSSrCache(),
            })
        }
    }

    public createPrerecords(prerecord: ICreatePrerecordModel) {
        return this.post<ICreatePrerecordModel, IValidationResult>('api/me/prerecords', prerecord, {
            // context: withRecapture('createPrerecords'),
        })
    }

    public cancelPrerecord(prerecordId: number) {
        return this.delete(`api/me/prerecords/${prerecordId}`)
    }

    public getPrerecordCalendarUri(prerecordId: number) {
        return this.getUri(`api/me/prerecords/${prerecordId}/calendar`)
    }

    public history() {
        return this.get<IPatientHistory[]>('api/history')
    }

    public historyByPatient(patientId: number) {
        return this.get<IPatientHistory[]>(`api/history/${patientId}`)
    }

    public analysisFiles(analysisId: number) {
        return this.get<IOnlineAnalysisFile[]>(`api/history/analysisFiles/${analysisId}`)
    }

    public getPrintOrderAnalysisUri() {
        return this.getUri(`api/order/print/analysis/conclusion.pdf`)
    }

    public getPrintOrderProtocolUri(protocolId: number) {
        return this.getUri(`api/order/print/protocol/${protocolId}/conclusion.pdf`)
    }

    public getPrintAnalysisUri(analysisId: number, context?: HttpContext) {
        return this.getUri(`api/history/print/analysis/${analysisId}/conclusion.pdf`)
    }

    public getprintProtocolUri(protocolId: number) {
        return this.getUri(`api/history/print/protocol/${protocolId}/conclusion.pdf`)
    }

    public getFamily() {
        return this.get<IFamilyMember[]>('api/family', {
            context: withSSrCache(),
        })
    }

    public getFamilyMember(patientId: number) {
        return this.get<IUserInformationModel>(`api/family/${patientId}`)
    }

    public updateFamilyMember(patientId: number, model: IUpdateUserInformationModel) {
        return this.post(`api/family/${patientId}`, model)
    }

    public registerFamilyMember(model: IRegisterFamilyMemberModel) {
        return this.post('api/family/register', model)
    }

    public removeFamilyMember(patientId: number) {
        return this.delete(`api/family/${patientId}`)
    }

    public getProtocolInformation(protocolId: number) {
        return this.get<OnlineProtocolInformation>(`api/history/protocols/${protocolId}/information`)
    }

    public getAnalysisInformation(analysisId: number) {
        return this.get<OnlineAnalysisInformation>(`api/history/analysis/${analysisId}/information`)
    }

    public getOrderInformation() {
        return this.get<IOrderInformation>('api/order/information')
    }

    public ordeLogOff() {
        return this.post('api/order/logoff', {})
    }

    public orderMe() {
        return this.get<IPatientProfile>('api/order/me', {})
    }

    public printOrderAnalysis(context?: HttpContext) {
        return this.download(`api/order/print/analysis`, withSpinner(context))
    }

    public downloadOrderAnalysisFile(analysisFileId: number, context?: HttpContext) {
        return this.download(`api/order/print/analysisFiles/${analysisFileId}`, withSpinner(context))
    }
    public printOrderProtocol(protocolId: number, context?: HttpContext) {
        return this.download(`api/order/print/protocol/${protocolId}`, withSpinner(context))
    }

    public meDiscount(): Observable<PatientDiscountDto[]> {
        return this.get<PatientDiscountDto[]>('api/me/discounts')
    }
    public onlineDoctors(): Observable<IOnlineDoctorSlim[]> {
        return this.get<IOnlineDoctorSlim[]>('api/v2/doctors', {
            context: withSSrCache(),
        }).pipe(map((results) => results.sort((x) => x.orderIndex)))
    }
    public onlineDoctor(id: number, context?: HttpContext): Observable<IOnlineDoctorFull> {
        return this.get<IOnlineDoctorFull>(`api/v2/doctors/${id}`, {
            context: withSSrCache({
                context: context,
                enabled: true,
            }),
        })
    }

    public packageCategories(
        parentId: number | null = null,
        searchExpr: string | null = ''
    ): Observable<IPackageCategorie[]> {
        let q = new HttpParams()

        if (parentId) {
            q = q.set('parentId', parentId)
        }
        if (searchExpr) {
            q = q.set('searchExpr', searchExpr)
        }

        return this.get<IPackageCategorie[]>('api/price/categories', {
            params: q,
        })
    }

    public packages(categoryId: number | null = null, searchExpr: string | null = ''): Observable<IPackageSlim[]> {
        let q = new HttpParams()

        if (categoryId) {
            q = q.set('categoryId', categoryId)
        }
        if (searchExpr) {
            q = q.set('searchExpr', searchExpr)
        }

        return this.get<IPackageSlim[]>('api/price/packages', {
            params: q,
            context: withSSrCache(),
        })
    }
    public package(id: number): Observable<IPackageFull> {
        return this.get<IPackageFull>(`api/price/packages/${id}`)
    }
}
